import {
  Card,
  CardHeader,
  PaxTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  SortableHeaderCell,
  useTranslation,
  useSortedTable,
  Stack,
  PaxInputSupportingText,
  TextField,
} from "@ncino/pax-ui";
import { createFileRoute, useRouteContext, Navigate } from "@tanstack/react-router";
import { PageHeader } from "@/shared/components/PageHeader";
import { PageContentContainer } from "@/shared/components/PageContentContainer";
import { ChangeEvent, useState } from "react";
import { useMyPortfoliosQuery } from "@/services/graphql/reactQuery";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import { UserIcon } from "@phosphor-icons/react";

export const Route = createFileRoute("/portfolios/")({
  staticData: { breadcrumb: { title: ({ t }) => t("breadcrumbs.portfolios"), icon: UserIcon } },
  component: RouteComponent,
});

type InstitutionRow = {
  id: number;
  primaryLoanPortfolioId?: number | null;
  instName: string;
  nameShort?: string | null;
  activeDataDate?: string | null;
};

function filterInstitutionBySearch(institution: InstitutionRow, searchValue: string) {
  const { instName, nameShort, primaryLoanPortfolioId: id } = institution || {};
  const matchingSearchVal = searchValue.toLowerCase();
  if (instName.toLowerCase().includes(matchingSearchVal)) return true;
  else if (!!nameShort && nameShort.toLowerCase().includes(matchingSearchVal)) return true;
  else if (!!id && id.toString().includes(matchingSearchVal)) return true;
  else return false;
}

function RouteComponent() {
  const { t } = useTranslation("portfolios");
  const theme = useTheme();
  const { portfolioId: activePortfolioId } = useRouteContext({ from: "/portfolios/" });

  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const updateValue = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (page !== 0) setPage(0);
    setSearchValue(e.target.value);
  };

  const { data, isLoading } = useMyPortfoliosQuery();
  const isAllAccess = data?.getMyPortfolios?.isAllAccess ?? false;
  const institutions = data?.getMyPortfolios?.institutions ?? [];

  const activeInstitution = institutions.find(
    (i) => i.primaryLoanPortfolioId === activePortfolioId
  );

  const restInstitutions = institutions.filter(
    (i) => i.primaryLoanPortfolioId !== activePortfolioId
  );

  const searchedInstitutions = restInstitutions.filter((i) =>
    filterInstitutionBySearch(i, searchValue)
  );

  const sortableTable = useSortedTable<InstitutionRow>(searchedInstitutions, "instName");

  const sortedInstitutions = activeInstitution
    ? [activeInstitution, ...sortableTable.items]
    : sortableTable.items;

  const paginatedInstitutions = sortedInstitutions.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  if (!isLoading && !isAllAccess) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <PageHeader title={t("portfolioPage.title")} subheader={t("portfolioPage.subheader")} />
      <PageContentContainer>
        <Card variant="outlined">
          <CardHeader
            title={t("portfolioPage.tableCardTitle")}
            subheader={t("portfolioPage.tableCardSubheader")}
          />
          <Stack direction="row" sx={{ padding: "16px", paddingTop: "0px" }} gap="16px">
            <PaxInputSupportingText title={t("portfolioPage.searchLabel")} sx={{ width: "50%" }}>
              <TextField
                size="small"
                sx={{ width: "100%" }}
                value={searchValue}
                onChange={updateValue}
                placeholder={t("portfolioPage.searchPlaceholder")}
              />
            </PaxInputSupportingText>
          </Stack>
          <PaxTable
            pagination={{
              count: sortedInstitutions.length,
              page,
              rowsPerPage,
              onPageChange: (newPage) => setPage(newPage),
              onRowsPerPageChange: (newRowsPerPage) => {
                setRowsPerPage(newRowsPerPage);
                setPage(0);
              },
            }}
          >
            <TableHead>
              <TableRow>
                <SortableHeaderCell
                  column="instName"
                  sortColumn={sortableTable.column}
                  sortDirection={sortableTable.direction}
                  onSort={(col) => {
                    sortableTable.sort(col);
                    setPage(0);
                  }}
                >
                  {t("portfolioPage.institutionNameColumn")}
                </SortableHeaderCell>
                <SortableHeaderCell
                  column="nameShort"
                  sortColumn={sortableTable.column}
                  sortDirection={sortableTable.direction}
                  onSort={(col) => {
                    sortableTable.sort(col);
                    setPage(0);
                  }}
                >
                  {t("portfolioPage.institutionShortNameColumn")}
                </SortableHeaderCell>
                <SortableHeaderCell
                  column="primaryLoanPortfolioId"
                  sortColumn={sortableTable.column}
                  sortDirection={sortableTable.direction}
                  onSort={(col) => {
                    sortableTable.sort(col);
                    setPage(0);
                  }}
                >
                  {t("portfolioPage.portfolioIdColumn")}
                </SortableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <CircularProgress aria-label="Loading…" />
                  </TableCell>
                </TableRow>
              ) : (
                paginatedInstitutions.map((institution) =>
                  institution.primaryLoanPortfolioId === activePortfolioId ? (
                    <TableRow
                      key={institution.primaryLoanPortfolioId}
                      sx={{
                        background: theme.palette.info.selected,
                        hover: theme.palette.info.hover,
                      }}
                    >
                      <TableCell sx={{ gap: "24px", display: "flex", alignItems: "center" }}>
                        {institution.instName}
                        <Chip
                          label={"Active"}
                          sx={{
                            background: theme.palette.info.main,
                            color: theme.palette.info.contrastText,
                          }}
                        ></Chip>
                      </TableCell>
                      <TableCell>{institution.nameShort}</TableCell>
                      <TableCell>{institution.primaryLoanPortfolioId}</TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={institution.primaryLoanPortfolioId}>
                      <TableCell>{institution.instName}</TableCell>
                      <TableCell>{institution.nameShort}</TableCell>
                      <TableCell>{institution.primaryLoanPortfolioId}</TableCell>
                    </TableRow>
                  )
                )
              )}
            </TableBody>
          </PaxTable>
        </Card>
      </PageContentContainer>
    </>
  );
}
